// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Bounded verified source loading, one-owner range persistence, and offline readiness. */

import { MULTIKEY_MAX_INPUT_BYTES } from "./multikey-worker-protocol";
import {
  MULTIKEY_MAX_INDEX_BYTES,
  parseMultikeyIndex,
  type MultikeyIndex,
  type MultikeyRange,
} from "./multikey-index";
import {
  MULTIKEY_LEGACY_SOURCE_PREFIX,
  MULTIKEY_RANGE_REQUEST_HEADER,
} from "./multikey-cache-policy";
import {
  beginMultikeySourceInventory,
  completeMultikeySourceInventory,
  MultikeySourceEditionError,
  persistMultikeySource,
  readMultikeySource,
  sweepLegacyMultikeySources,
  type MultikeySourceInventory,
  type MultikeySourceStorage,
} from "./multikey-source-cache";

type ResourceCache = Pick<CacheStorage, "match">;
type SourceCache = MultikeySourceStorage;

/** Optional browser resource services; offline never attempts a network source download. */
export interface MultikeyResourceOptions {
  fetcher?: typeof fetch;
  cacheStorage?: SourceCache;
  offline?: boolean;
  hashWorkerUrl?: string;
  /** Edition claimed by the fresh index load; old readers never renew this lease implicitly. */
  inventory?: MultikeySourceInventory;
  /** Explicit ordinary PDF URL, never the index's informational canonical filename. */
  canonicalUrl?: string;
  /** Leave the current inventory untouched until the caller verifies its canonical bytes. */
  deferInventoryClaim?: boolean;
  /** Reports actual range fetch/body-read activity, excluding cache reads and verification. */
  onRangeDownload?: (active: boolean) => void;
}

/** Range-source persistence only, independent of ordinary reading and generated artifacts. */
export interface MultikeySourceStatus {
  completed: number;
  total: number;
  bytes: number;
  /** Preparation includes cache verification; downloading is limited to network source reads. */
  state: "preparing" | "downloading" | "ready" | "partial";
}

const INDEX_CACHE = "sb-runtime-v2";
let prefetchTail: Promise<void> = Promise.resolve();
const pendingIndexes = new WeakMap<MultikeyIndex, { blob: Blob; headers: Headers }>();
class SourceIntegrityError extends Error {}

function availableCache(): SourceCache | undefined {
  try {
    return globalThis.caches;
  } catch {
    return undefined;
  }
}

async function cachedResponse(
  url: string,
  cache: ResourceCache | undefined,
): Promise<Response | undefined> {
  try {
    return await cache?.match(url, { ignoreVary: true });
  } catch {
    return undefined;
  }
}

/** Uses normal credentials/ETag/SW handling, with cache fallback only for network failure. */
async function sourceResponse(
  url: string,
  signal: AbortSignal,
  options: MultikeyResourceOptions,
): Promise<Response> {
  signal.throwIfAborted();
  const cache = options.cacheStorage ?? availableCache();
  let response = options.offline ? await cachedResponse(url, cache) : undefined;
  if (options.offline && !response) throw new Error(`Multikey source unavailable offline: ${url}`);
  if (!response) {
    try {
      response = await (options.fetcher ?? fetch)(url, {
        signal,
        credentials: "same-origin",
        cache: "no-cache",
      });
    } catch (error) {
      signal.throwIfAborted();
      response = await cachedResponse(url, cache);
      if (!response) throw error;
    }
  }
  return response;
}

/** Revalidates the configured JSON before interpreting its exact bounded bytes. */
export async function loadMultikeyIndex(
  url: string,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<{
  index: MultikeyIndex;
  identity: string;
  inventory?: MultikeySourceInventory;
  previousIndex?: MultikeyIndex;
  deferredInventoryClaim?: boolean;
}> {
  const storage = options.cacheStorage ?? availableCache();
  let previous: MultikeyIndex | undefined;
  try {
    const cached = await cachedResponse(url, storage);
    if (cached)
      previous = parseMultikeyIndex(
        await (await responseBlob(cached, signal, MULTIKEY_MAX_INDEX_BYTES)).text(),
        url,
      );
  } catch {
    signal.throwIfAborted();
  }
  const response = await sourceResponse(url, signal, options);
  const blob = await responseBlob(response, signal, MULTIKEY_MAX_INDEX_BYTES);
  const index = parseMultikeyIndex(await blob.text(), url);
  const identity = await hashMultikeyBlob(blob, signal, options);
  if (options.deferInventoryClaim) pendingIndexes.set(index, { blob, headers: response.headers });
  const inventory = options.deferInventoryClaim
    ? undefined
    : await claimMultikeySourceInventory(index, previous, signal, options);
  if (!options.deferInventoryClaim)
    await persistSource(INDEX_CACHE, url, blob, response.headers, signal, options);
  return {
    index,
    identity,
    inventory,
    previousIndex: previous,
    deferredInventoryClaim: options.deferInventoryClaim,
  };
}

/** Claims an index only after its caller has verified the configured canonical PDF. */
export async function claimMultikeySourceInventory(
  index: MultikeyIndex,
  previous: MultikeyIndex | undefined,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<MultikeySourceInventory | undefined> {
  const inventory = await beginMultikeySourceInventory(
    index,
    previous,
    options.cacheStorage ?? availableCache(),
    signal,
    options.canonicalUrl,
  );
  const pending = pendingIndexes.get(index);
  if (pending) {
    // Retained references must be durable before a paired index displaces its predecessor.
    if (
      !inventory ||
      !(await persistSource(INDEX_CACHE, index.url, pending.blob, pending.headers, signal, options))
    )
      return undefined;
    pendingIndexes.delete(index);
  }
  return inventory;
}

/** Verifies only complete cached ordinary bytes; maintenance must not fetch or reload a reader. */
export async function verifyCachedMultikeyCanonical(
  canonicalUrl: string,
  index: MultikeyIndex,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<void> {
  const response = await cachedResponse(canonicalUrl, options.cacheStorage ?? availableCache());
  if (!response) throw new Error("Multikey canonical PDF is not cached");
  await verifyMultikeyCanonical(
    await responseBlob(response, signal, index.canonical.byteLength),
    index,
    signal,
    options,
  );
}

/** Checks exact ordinary bytes; an exclusive Uint8Array transfers ownership and is detached. */
export async function verifyMultikeyCanonical(
  blob: Blob | Uint8Array,
  index: MultikeyIndex,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<void> {
  await verifyBlob(blob, index.canonical, signal, options);
}

/** Hashes bounded slices in a standalone worker; no hashing/PDF runtime enters app.js. */
export async function hashMultikeyBlob(
  blob: Blob | Uint8Array,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<string> {
  signal.throwIfAborted();
  const url = options.hashWorkerUrl ?? "/assets/multikey-hash-worker.js";
  const cached = await cachedMultikeyWorker(
    url,
    signal,
    options.cacheStorage ?? availableCache(),
    bytes => URL.createObjectURL(new Blob([bytes as BlobPart], { type: "text/javascript" })),
    objectUrl => URL.revokeObjectURL(objectUrl),
  );
  let worker: Worker | undefined;
  try {
    signal.throwIfAborted();
    worker = new Worker(cached?.url ?? url, { type: "module" });
    const active = worker;
    const identity = await new Promise<string>((resolve, reject) => {
      const abort = () => settle(signal.reason);
      signal.addEventListener("abort", abort, { once: true });
      const timeout = setTimeout(
        () => settle(new Error("Multikey source hashing timed out")),
        120_000,
      );
      const settle = (error?: unknown, identity?: string) => {
        clearTimeout(timeout);
        signal.removeEventListener("abort", abort);
        if (error !== undefined) reject(error);
        else resolve(identity!);
      };
      active.onmessage = event => {
        const data: unknown = event.data;
        if (typeof data === "string" && /^[a-f0-9]{64}$/.test(data)) settle(undefined, data);
        else settle(new Error("Multikey source hashing failed"));
      };
      active.onerror = () => settle(new Error("Multikey hash worker unavailable"));
      active.onmessageerror = () => settle(new Error("Invalid multikey hash worker response"));
      try {
        active.postMessage(blob, blob instanceof Uint8Array ? [blob.buffer as ArrayBuffer] : []);
      } catch (error) {
        settle(error);
      }
    });
    signal.throwIfAborted();
    return identity;
  } finally {
    worker?.terminate();
    cached?.release();
  }
}

async function verifyBlob(
  blob: Blob | Uint8Array,
  expected: { byteLength: number; identity: string },
  signal: AbortSignal,
  options: MultikeyResourceOptions,
): Promise<void> {
  signal.throwIfAborted();
  if ((blob instanceof Uint8Array ? blob.byteLength : blob.size) !== expected.byteLength)
    throw new SourceIntegrityError("Multikey source byte length mismatch");
  if ((await hashMultikeyBlob(blob, signal, options)) !== expected.identity)
    throw new SourceIntegrityError("Multikey source SHA-256 mismatch");
}

async function persistSource(
  name: string,
  url: string,
  blob: Blob,
  originalHeaders: Headers,
  signal: AbortSignal,
  options: MultikeyResourceOptions,
): Promise<boolean> {
  signal.throwIfAborted();
  try {
    const cache = await (options.cacheStorage ?? availableCache())?.open?.(name);
    if (!cache) return false;
    const headers = new Headers(originalHeaders);
    headers.delete("content-encoding");
    headers.delete("content-range");
    headers.delete("vary");
    headers.set("content-length", String(blob.size));
    await cache.put(url, new Response(blob, { headers }));
    signal.throwIfAborted();
    return true;
  } catch {
    // Foreground reading may proceed; prefetch separately checks actual persistence.
    signal.throwIfAborted();
    return false;
  }
}

/** Prefers verified current bytes; a same-URL replacement never authorizes an old index's hash. */
export async function loadMultikeyRange(
  range: MultikeyRange,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<Blob> {
  signal.throwIfAborted();
  const storage = options.cacheStorage ?? availableCache();
  let cached: Response | undefined;
  try {
    cached = await readMultikeySource(range, storage, signal, options.inventory);
  } catch (error) {
    signal.throwIfAborted();
    if (error instanceof MultikeySourceEditionError) throw error;
  }
  if (cached) {
    try {
      const blob = await responseBlob(cached, signal, range.byteLength);
      await verifyBlob(blob, range, signal, options);
      return blob;
    } catch (error) {
      signal.throwIfAborted();
      if (!(error instanceof SourceIntegrityError)) throw error;
      // A mismatch can be a valid newer edition, not corruption. Never delete it on read failure.
      if (options.offline) throw error;
    }
  }
  const persist = async (blob: Blob, headers: Headers) => {
    if (options.deferInventoryClaim && !options.inventory) return;
    try {
      await persistMultikeySource(
        range,
        blob,
        headers,
        storage,
        signal,
        async response => {
          await verifyBlob(
            await responseBlob(response, signal, range.byteLength),
            range,
            signal,
            options,
          );
        },
        options.inventory,
      );
    } catch (error) {
      signal.throwIfAborted();
      if (error instanceof MultikeySourceEditionError) throw error;
      // Cache denial must not prevent verified online processing. Prefetch checks actual persistence.
    }
  };
  // Lazily adopt deployed hash caches/runtime copies without trusting their URL or cache name.
  for (const name of [MULTIKEY_LEGACY_SOURCE_PREFIX + range.identity, INDEX_CACHE]) {
    let legacy: Response | undefined;
    try {
      if (storage?.keys && !(await storage.keys()).includes(name)) continue;
      legacy = await (await storage?.open?.(name))?.match(range.url, { ignoreVary: true });
    } catch {
      /* denied */
    }
    if (!legacy) continue;
    try {
      const blob = await responseBlob(legacy, signal, range.byteLength);
      await verifyBlob(blob, range, signal, options);
      await persist(blob, legacy.headers);
      return blob;
    } catch (error) {
      signal.throwIfAborted();
      if (!(error instanceof SourceIntegrityError)) throw error;
    }
  }
  if (options.offline) throw new Error(`Multikey source unavailable offline: ${range.url}`);
  let response: Response;
  let blob: Blob;
  options.onRangeDownload?.(true);
  try {
    response = await (options.fetcher ?? fetch)(range.url, {
      signal,
      credentials: "same-origin",
      cache: "no-cache",
      // Cross-origin responses were never generic-SW cacheable; avoid introducing a CORS preflight.
      headers:
        !globalThis.location || new URL(range.url).origin === location.origin
          ? { [MULTIKEY_RANGE_REQUEST_HEADER]: "1" }
          : undefined,
    });
    blob = await responseBlob(response, signal, range.byteLength);
  } finally {
    options.onRangeDownload?.(false);
  }
  await verifyBlob(blob, range, signal, options);
  await persist(blob, response.headers);
  return blob;
}

/** Expands the advertised dynamic inventory serially, without PDF parsing or retaining all source Blobs. */
export async function prefetchMultikeyRanges(
  index: MultikeyIndex,
  signal: AbortSignal,
  onProgress: (status: MultikeySourceStatus) => void,
  options: MultikeyResourceOptions = {},
): Promise<void> {
  let started = false;
  let abort: () => void = () => {};
  const cancelled = new Promise<never>((_, reject) => {
    abort = () => {
      if (!started) {
        onProgress({ completed: 0, total: index.ranges.length, bytes: 0, state: "partial" });
        reject(signal.reason);
      }
    };
    signal.addEventListener("abort", abort, { once: true });
  });
  const task = prefetchTail.then(() => {
    signal.throwIfAborted();
    started = true;
    // Bound temporary replacement space to one prefetching book across cooperating tabs.
    if (globalThis.navigator?.locks)
      return navigator.locks.request("sb-multikey-source-prefetch-v2", { signal }, () =>
        prefetchInventory(index, signal, onProgress, options),
      );
    return prefetchInventory(index, signal, onProgress, options);
  });
  prefetchTail = task.catch(() => {});
  try {
    if (signal.aborted) abort();
    await Promise.race([task, cancelled]);
  } finally {
    signal.removeEventListener("abort", abort);
  }
}

async function prefetchInventory(
  index: MultikeyIndex,
  signal: AbortSignal,
  onProgress: (status: MultikeySourceStatus) => void,
  options: MultikeyResourceOptions,
): Promise<void> {
  const storage = options.cacheStorage ?? availableCache();
  options = {
    ...options,
    inventory:
      options.inventory ??
      (options.deferInventoryClaim
        ? undefined
        : await claimMultikeySourceInventory(index, undefined, signal, options)),
  };
  let completed = 0;
  let bytes = 0;
  const report = (state: MultikeySourceStatus["state"]) =>
    onProgress({ completed, total: index.ranges.length, bytes, state });
  report("preparing");
  let failure: unknown;
  try {
    if (options.deferInventoryClaim && !options.inventory)
      throw new Error("Multikey source inventory persistence unavailable");
    for (const range of index.ranges) {
      signal.throwIfAborted();
      try {
        await loadMultikeyRange(range, signal, {
          ...options,
          onRangeDownload: active => report(active ? "downloading" : "preparing"),
        });
        const cached = await readMultikeySource(range, storage, signal, options.inventory);
        if (!cached) throw new Error("Multikey source cache storage unavailable");
        await verifyBlob(
          await responseBlob(cached, signal, range.byteLength),
          range,
          signal,
          options,
        );
        completed++;
        bytes += range.byteLength;
      } catch (error) {
        signal.throwIfAborted();
        failure ??= error;
      }
      report("preparing");
    }
    // Recheck presence after the entire pass: quota eviction can remove earlier successful writes.
    completed = 0;
    bytes = 0;
    for (const range of index.ranges) {
      signal.throwIfAborted();
      const cached = await readMultikeySource(range, storage, signal, options.inventory);
      if (cached) {
        await verifyBlob(
          await responseBlob(cached, signal, range.byteLength),
          range,
          signal,
          options,
        );
        completed++;
        bytes += range.byteLength;
      }
    }
    if (failure || completed !== index.ranges.length)
      throw failure ?? new Error("Multikey source cache is incomplete");
    await completeMultikeySourceInventory(
      storage,
      signal,
      async (response, range) => {
        await verifyBlob(
          await responseBlob(response, signal, range.byteLength),
          range,
          signal,
          options,
        );
      },
      index,
      options.inventory,
    );
    await sweepLegacyRanges(index, signal, options);
    report("ready");
  } catch (error) {
    report("partial");
    throw error;
  }
}

/** Legacy caches have no book IDs; cached valid indices protect unvisited books during migration. */
async function sweepLegacyRanges(
  index: MultikeyIndex,
  signal: AbortSignal,
  options: MultikeyResourceOptions,
): Promise<void> {
  const storage = options.cacheStorage ?? availableCache();
  try {
    if (!(await storage?.keys?.())?.some(name => name.startsWith(MULTIKEY_LEGACY_SOURCE_PREFIX)))
      return;
    const indexes = [index];
    const runtime = await storage?.open?.(INDEX_CACHE);
    for (const request of (await runtime?.keys()) ?? []) {
      if (!new URL(request.url).pathname.toLowerCase().endsWith(".json")) continue;
      const response = await runtime!.match(request);
      if (!response) continue;
      let text: string;
      let raw: unknown;
      try {
        text = await (await responseBlob(response, signal, MULTIKEY_MAX_INDEX_BYTES)).text();
        raw = JSON.parse(text);
      } catch {
        signal.throwIfAborted();
        continue;
      }
      if (
        !raw ||
        typeof raw !== "object" ||
        (raw as { format?: string }).format !== "ulsbs:multikey-ranges"
      )
        continue;
      indexes.push(parseMultikeyIndex(text, request.url));
    }
    await sweepLegacyMultikeySources(storage, indexes, signal);
  } catch {
    signal.throwIfAborted();
    // Unreadable inventory/storage is not authority to remove another book's offline sources.
  }
}

/** Rechecks persisted sources/runtime after eviction or updates, independently of download progress. */
export async function checkMultikeyOfflineReady(
  index: MultikeyIndex,
  canonicalUrl: string,
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<boolean> {
  signal.throwIfAborted();
  const storage = options.cacheStorage ?? availableCache();
  const runtimeUrls = [
    "/assets/app.js",
    "/assets/app.css",
    "/assets/song-index-worker.js",
    "/assets/multikey-worker.js",
    "/assets/multikey-export-worker.js",
    options.hashWorkerUrl ?? "/assets/multikey-hash-worker.js",
    "/assets/multikey-worker.LICENSE.txt",
    "/assets/pdf.worker.min.mjs",
    ...["Regular", "Bold", "Italic", "BoldItalic"].map(
      name => `/assets/fonts/Ubuntu-${name}.woff2`,
    ),
    ...[
      "FoxitDingbats.pfb",
      "FoxitFixedBoldItalic.pfb",
      "FoxitFixedBold.pfb",
      "FoxitFixedItalic.pfb",
      "FoxitFixed.pfb",
      "FoxitSerifBoldItalic.pfb",
      "FoxitSerifBold.pfb",
      "FoxitSerifItalic.pfb",
      "FoxitSerif.pfb",
      "FoxitSymbol.pfb",
      "LiberationSans-BoldItalic.ttf",
      "LiberationSans-Bold.ttf",
      "LiberationSans-Italic.ttf",
      "LiberationSans-Regular.ttf",
    ].map(name => `/assets/pdfjs-standard-fonts/${name}`),
  ];
  try {
    for (const url of runtimeUrls) {
      signal.throwIfAborted();
      const response = await cachedResponse(url, storage);
      if (!response || response.status !== 200 || response.headers.has("content-range"))
        return false;
      void response.body?.cancel().catch(() => {});
    }
    const json = await cachedResponse(index.url, storage);
    if (!json) return false;
    const jsonBlob = await responseBlob(json, signal, MULTIKEY_MAX_INDEX_BYTES);
    if (
      JSON.stringify(parseMultikeyIndex(await jsonBlob.text(), index.url)) !== JSON.stringify(index)
    )
      return false;
    const canonical = await cachedResponse(canonicalUrl, storage);
    if (!canonical) return false;
    await verifyBlob(
      await responseBlob(canonical, signal, index.canonical.byteLength),
      index.canonical,
      signal,
      options,
    );
    for (const range of index.ranges) {
      const response = await readMultikeySource(range, storage, signal, options.inventory);
      if (!response) return false;
      await verifyBlob(
        await responseBlob(response, signal, range.byteLength),
        range,
        signal,
        options,
      );
    }
    return true;
  } catch {
    signal.throwIfAborted();
    return false;
  }
}

/** Streams into browser-owned Blob backing, bounding bytes before buffering and avoiding JS chunk arrays. */
async function responseBlob(response: Response, signal: AbortSignal, limit: number): Promise<Blob> {
  if (signal.aborted) void response.body?.cancel().catch(() => {});
  signal.throwIfAborted();
  if (response.status !== 200 || response.headers.has("content-range")) {
    void response.body?.cancel().catch(() => {});
    throw new SourceIntegrityError("Multikey source requires a complete successful response");
  }
  const declared = Number(response.headers.get("content-length"));
  if (declared > limit || limit > MULTIKEY_MAX_INPUT_BYTES) {
    void response.body?.cancel().catch(() => {});
    throw new SourceIntegrityError("Multikey source exceeds the input byte limit");
  }
  if (!response.body) throw new SourceIntegrityError("Multikey source response has no body");
  let total = 0;
  const bounded = response.body.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        signal.throwIfAborted();
        total += chunk.byteLength;
        if (total > limit)
          throw new SourceIntegrityError("Multikey source exceeds the input byte limit");
        controller.enqueue(chunk);
      },
    }),
    { signal },
  );
  const blob = await new Response(bounded).blob();
  signal.throwIfAborted();
  return blob;
}

/** Boots a cached standalone worker, preserving its asset-relative source-map location. */
export async function cachedMultikeyWorker(
  url: string,
  signal: AbortSignal,
  cache: ResourceCache | undefined,
  createUrl: (data: Uint8Array) => string,
  revokeUrl: (url: string) => void,
): Promise<{ url: string; release(): void } | null> {
  signal.throwIfAborted();
  const response = await cachedResponse(url, cache);
  if (!response) return null;
  const data = await readCompleteResponse(
    response,
    signal,
    4 * 1024 * 1024,
    "Multikey worker asset",
  );
  signal.throwIfAborted();
  // Blob URLs have no asset directory for DevTools to resolve an external map against.
  const script = new TextDecoder()
    .decode(data)
    .replace(
      /^\/\/# sourceMappingURL=(\S+)[ \t]*\r?$/gm,
      (_comment, map: string) =>
        `//# sourceMappingURL=${
          new URL(map, response.url || new URL(url, globalThis.location?.href)).href
        }`,
    );
  const objectUrl = createUrl(new TextEncoder().encode(script));
  let released = false;
  return {
    url: objectUrl,
    release: () => {
      if (released) return;
      released = true;
      revokeUrl(objectUrl);
    },
  };
}

/** Reads the small cached worker script with bounded bytes and abortable stream ownership. */
async function readCompleteResponse(
  response: Response,
  signal: AbortSignal,
  limit: number,
  label: string,
): Promise<Uint8Array> {
  if (signal.aborted) {
    void response.body?.cancel().catch(() => {});
    signal.throwIfAborted();
  }
  if (response.status !== 200 || response.headers.has("content-range")) {
    void response.body?.cancel().catch(() => {});
    throw new Error(`${label} requires a complete successful response`);
  }
  const length = Number(response.headers.get("content-length"));
  if (Number.isFinite(length) && length > limit) {
    void response.body?.cancel().catch(() => {});
    throw new Error(`${label} exceeds the input byte limit`);
  }
  if (!response.body) throw new Error(`${label} response has no body`);
  const reader = response.body.getReader();
  const abort = (): void => {
    void reader.cancel(signal.reason).catch(() => {});
  };
  signal.addEventListener("abort", abort, { once: true });
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      signal.throwIfAborted();
      const { value, done } = await reader.read();
      signal.throwIfAborted();
      if (done) break;
      total += value.byteLength;
      if (total > limit) throw new Error(`${label} exceeds the input byte limit`);
      chunks.push(value);
    }
    signal.throwIfAborted();
    const data = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      data.set(chunk, offset);
      offset += chunk.length;
    }
    return data;
  } catch (error) {
    void reader.cancel().catch(() => {});
    throw error;
  } finally {
    signal.removeEventListener("abort", abort);
    reader.releaseLock();
  }
}
